<template>
  <v-sheet
    class="content-panel elevation-2"
    :class="panelClasses"
    :style="panelStyle"
    color="bg_Greeny_Gray"
    role="dialog"
    :aria-label="panelTitle"
  >
    <!-- Header -->
    <PanelHeader
      :title="panelTitle"
      :show-back="showBackButton"
      :show-close="true"
      @back="handleBack"
      @close="handleClose"
    >
      <template #actions>
        <!-- Copy Tag URL button — resolved through the same spatial intent as Zoom.
             A concrete plan copies immediately; an ambiguous plan opens the
             authoritative facility picker returned by the resolver.
             Post-copy: icon / color / tooltip swap to a "Copied!" state
             for ~1.5s as tactile feedback (no toast — button-local only). -->
        <v-tooltip
          v-if="canCopyTagUrl"
          bottom
          :open-delay="300"
        >
          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <v-menu
              v-model="showFacilityMenu"
              offset-y
              :close-on-content-click="true"
            >
              <template v-slot:activator="{ attrs: menuAttrs }">
                <v-btn
                  icon
                  small
                  class="copy-url-btn"
                  :class="{ 'copy-success': justCopied }"
                  :color="justCopied ? undefined : copyButtonColor"
                  v-bind="{
                    ...tooltipAttrs,
                    ...menuAttrs,
                  }"
                  v-on="tooltipOn"
                  @click="handleCopyUrlClick"
                >
                  <v-icon size="16">
                    {{ justCopied ? 'mdi-check-circle' : 'mdi-link-variant' }}
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense v-if="copyUrlAlternatives.length > 0">
                <v-subheader>Copy URL for…</v-subheader>
                <v-list-item
                  v-for="alternative in copyUrlAlternatives"
                  :key="alternative.facilityId"
                  class="copy-url-menu-item"
                  @click="copyUrlForFacility(alternative.facilityId)"
                >
                  <v-list-item-title>
                    {{ facilityName(alternative.facilityId) }}
                  </v-list-item-title>
                  <v-list-item-action>
                    <v-chip x-small>{{ alternative.viewer === '360' ? '360' : '3D' }}</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <span>{{ copyButtonTooltip }}</span>
        </v-tooltip>

        <!-- Pin Button -->
        <v-tooltip bottom :open-delay="300">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click="togglePin"
              class="pin-btn"
              :class="{ 'pin-active': isPinned }"
            >
              <v-icon size="16">
                {{ isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ isPinned ? 'Unpin panel' : 'Pin panel (prevent replacement)' }}</span>
        </v-tooltip>
        <slot name="header-actions" />
      </template>
    </PanelHeader>

    <!-- Dynamic Panel Content -->
    <div class="panel-body" ref="panelContent">
      <!-- Async component with Vue 2 pattern -->
      <component
        v-if="panelComponent"
        :is="panelComponent"
        :ref="panelId"
        v-bind="panelProps"
      />

      <!-- Fallback for unknown panels -->
      <div v-else class="panel-not-found">
        <v-icon size="48" color="grey">mdi-alert-circle-outline</v-icon>
        <p class="mt-2 text--secondary">Panel not found: {{ panelId }}</p>
      </div>
    </div>

    <!-- Resize Handle (only for non-last panels or when explicitly enabled) -->
    <ResizeHandle
      v-if="showResizeHandle"
      :min-width="minWidth"
      :max-width="maxWidth"
      :current-width="width"
      @resize="handleResize"
      @resize-start="handleResizeStart"
      @resize-end="handleResizeEnd"
    />
  </v-sheet>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  PANEL_COMPONENTS,
  isHierarchicalPanel,
  getDynamicTitleConfig,
  getItemById,
} from '@Share/config/menuConfig';
import {
  PANEL_IDS,
  isTagTypePanel,
  getTagTypeFromPanelId,
} from '@/constants/panelIds';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import PanelHeader from './PanelHeader.vue';
import ResizeHandle from './ResizeHandle.vue';

/**
 * ContentPanel Component
 *
 * A resizable content panel that displays dynamic content based on panelId.
 * Part of the multi-panel navigation system.
 *
 * @props {String} panelId - Unique identifier for the panel content
 * @props {Number} width - Current width of the panel
 * @props {Number} leftOffset - Calculated left position from panel stack
 * @props {Number} position - Panel position in the stack (0-based)
 * @props {Boolean} isLast - Whether this is the rightmost panel
 *
 * @emits resize - When panel is being resized { panelId, width }
 * @emits resize-end - When resize operation completes
 * @emits close - When close button is clicked
 * @emits back - When back button is clicked
 */
export default {
  name: 'ContentPanel',

  components: {
    PanelHeader,
    ResizeHandle,
  },

  props: {
    /**
     * ID of the panel to display (e.g., 'tags', 'global-search')
     */
    panelId: {
      type: String,
      required: true,
    },

    /**
     * Current width of the panel
     */
    width: {
      type: Number,
      default: 300,
    },

    /**
     * Left offset calculated from previous panels
     */
    leftOffset: {
      type: Number,
      default: 0,
    },

    /**
     * Position in the panel stack (0-based index)
     */
    position: {
      type: Number,
      default: 0,
    },

    /**
     * Whether this is the last (rightmost) panel
     */
    isLast: {
      type: Boolean,
      default: true,
    },

    /**
     * Minimum panel width
     */
    minWidth: {
      type: Number,
      default: 200,
    },

    /**
     * Maximum panel width
     */
    maxWidth: {
      type: Number,
      default: 500,
    },
  },

  data() {
    return {
      isResizing: false,
      // Drives the Copy Tag URL button's post-click visual effect
      // (icon/color/tooltip swap for ~1.5s after a successful copy).
      justCopied: false,
      // v-model and resolver-returned choices for the facility picker menu.
      showFacilityMenu: false,
      copyUrlAlternatives: [],
      copyLinkRequestToken: 0,
    };
  },

  computed: {
    ...mapGetters({
      editingTag: 'GET_EDITING_TAG',
      editingAmisTag: 'GET_EDITING_AMIS_TAG',
    }),

    ...mapGetters('TAGS_TYPES', {
      selectedType: 'GET_SELECTED_TYPE',
    }),

    ...mapGetters('TAGS_GROUPS', {
      selectedGroup: 'GET_SELECTED_GROUP',
    }),

    ...mapGetters('GENERAL', {
      railWidth: 'GET_RAIL_WIDTH',
    }),

    /**
     * Panel position and size styles
     */
    panelStyle() {
      return {
        width: `${this.width}px`,
        left: `calc(var(--ds-nav-rail-collapsed) + ${this.leftOffset}px)`,
        zIndex: 7 - this.position, // Higher position = lower z-index
      };
    },

    /**
     * Panel CSS classes
     */
    panelClasses() {
      return {
        'is-first': this.position === 0,
        'is-last': this.isLast,
        'is-resizing': this.isResizing,
      };
    },

    /**
     * Get the menu item config for this panel
     */
    panelConfig() {
      return getItemById(this.panelId);
    },

    /**
     * Dynamic panel title based on context
     * For hierarchical panels, shows the editing item name
     */
    panelTitle() {
      const dynamicConfig = getDynamicTitleConfig(this.panelId);

      if (dynamicConfig) {
        // Check for editing state
        if (this.panelId === PANEL_IDS.TAGS && this.editingTag) {
          return this.editingTag.name || dynamicConfig.fallback;
        }
        if (this.panelId === PANEL_IDS.CONDITION_MAP && this.editingAmisTag) {
          return this.editingAmisTag.name || dynamicConfig.fallback;
        }
        return dynamicConfig.fallback;
      }

      // Use config title or fallback
      return this.panelConfig?.title || this.panelId;
    },

    canCopyTagUrl() {
      return this.panelId === PANEL_IDS.TAGS && !!this.editingTag;
    },

    /**
     * Warning (orange) color when the URL points to another facility.
     * Mirrors TagCard.vue's isolateButtonColor / zoomButtonColor pattern.
     * Returns undefined for the default primary color.
     */
    copyButtonColor() {
      return this.copyUrlAlternatives.length > 0 ? 'warning' : undefined;
    },

    /**
     * Reactive tooltip. Includes facility name for navigate-single and flips
     * to "Copied!" during the post-click effect.
     */
    copyButtonTooltip() {
      if (this.justCopied) return 'Copied!';
      if (this.copyUrlAlternatives.length > 0) return 'Copy tag URL — pick a facility';
      return 'Copy tag URL';
    },

    /**
     * Lazy-loaded panel component
     * Note: Using simple async component pattern to avoid Vue runtime template compilation issues.
     * The runtime-only Vue build cannot compile inline template strings.
     */
    panelComponent() {
      const loader = PANEL_COMPONENTS[this.panelId];
      if (!loader) {
        return null;
      }

      // Simple async component - Vue handles loading states gracefully
      return () => loader();
    },

    /**
     * Extract props to pass to the panel component based on panel ID.
     * Used for type-specific tag panels (tags-equipment, tags-pipeline, etc.)
     */
    panelProps() {
      if (isTagTypePanel(this.panelId)) {
        // Extract tag type from panel ID (e.g., 'tags-equipment' → 'Equipment')
        const tagType = getTagTypeFromPanelId(this.panelId);
        return { tagType };
      }
      return {};
    },

    /**
     * Whether this panel has hierarchical navigation
     */
    isHierarchical() {
      return isHierarchicalPanel(this.panelId);
    },

    /**
     * Whether we're at the root level of a hierarchical panel
     */
    isAtRootLevel() {
      if (this.panelId === PANEL_IDS.TAGS) {
        return !this.editingTag && !this.selectedType && !this.selectedGroup;
      }
      if (this.panelId === PANEL_IDS.CONDITION_MAP) {
        return !this.editingAmisTag;
      }
      return true;
    },

    /**
     * Whether this panel is pinned (protected from replacement)
     */
    isPinned() {
      return this.$store.getters['GENERAL/GET_IS_PANEL_PINNED'](this.panelId);
    },

    /**
     * Show back button for hierarchical panels when not at root
     */
    showBackButton() {
      return this.isHierarchical && !this.isAtRootLevel;
    },

    /**
     * Show resize handle (always show, even for last panel)
     */
    showResizeHandle() {
      return true;
    },
  },

  watch: {
    /**
     * When panel changes, scroll to top
     */
    panelId() {
      this.$nextTick(() => {
        if (this.$refs.panelContent) {
          this.$refs.panelContent.scrollTop = 0;
        }
      });
    },
    editingTag: {
      handler(newTag, oldTag) {
        if (newTag?.id === oldTag?.id) return;
        this.copyLinkRequestToken += 1;
        this.copyUrlAlternatives = [];
        this.showFacilityMenu = false;
        this.justCopied = false;
        if (this.copyEffectTimer) clearTimeout(this.copyEffectTimer);
      },
    },
  },

  beforeDestroy() {
    // Clear the copy-effect revert timer so it doesn't try to mutate
    // reactive state on a destroyed instance if the panel closes
    // within the 1.5s post-copy window.
    if (this.copyEffectTimer) clearTimeout(this.copyEffectTimer);
  },

  methods: {
    /**
     * Handle back button click
     * For hierarchical panels, navigate within the panel first
     */
    handleBack() {
      // Hierarchical back navigation is handled by CLOSE_PANEL action
      this.$store.dispatch('GENERAL/CLOSE_PANEL', this.panelId);
      this.$emit('back', this.panelId);
    },

    /**
     * Handle close button click
     * Uses forceClose to bypass step-back logic and close the panel immediately
     */
    handleClose() {
      this.$store.dispatch('GENERAL/CLOSE_PANEL', {
        panelId: this.panelId,
        forceClose: true,
      });
      this.$emit('close', this.panelId);
    },

    /**
     * Toggle pin state for this panel
     */
    togglePin() {
      this.$store.commit('GENERAL/TOGGLE_PANEL_PIN', this.panelId);
    },

    /**
     * Handle resize from ResizeHandle
     */
    handleResize(newWidth) {
      this.$emit('resize', { panelId: this.panelId, width: newWidth });
    },

    /**
     * Handle resize start
     */
    handleResizeStart() {
      this.isResizing = true;
      this.$emit('resize-start', this.panelId);
    },

    /**
     * Handle resize end
     */
    handleResizeEnd() {
      this.isResizing = false;
      // Persist the width to Vuex
      this.$store.dispatch('GENERAL/RESIZE_PANEL', {
        panelId: this.panelId,
        width: this.width,
      });
      this.$emit('resize-end', this.panelId);
    },

    async handleCopyUrlClick() {
      if (!this.canCopyTagUrl) return;
      const request = this.beginCopyLinkRequest();
      if (!request) return;
      this.copyUrlAlternatives = [];
      this.showFacilityMenu = false;
      try {
        const copyOutcome = await this.$store.dispatch(
          'NAVIGATE_TAG_SPATIALLY',
          this.copyLinkPayload(request.tagId),
        );
        if (!this.isCurrentCopyLinkRequest(request)) return;
        await this.applyCopyLinkOutcome(copyOutcome, request);
      } catch (err) {
        if (!this.isCurrentCopyLinkRequest(request)) return;
        debugError('Tag URL resolution failed', err);
        this.reportCopyLinkFailure();
      }
    },

    /**
     * Builds and copies the URL for a specific facility, then fires the
     * post-click visual effect. Used directly for single-target behaviors
     * and called from each menu item for navigate-menu.
     */
    async copyUrlForFacility(facilityId) {
      if (!facilityId || !this.editingTag) return;
      const request = this.beginCopyLinkRequest();
      if (!request) return;
      try {
        const copyOutcome = await this.$store.dispatch(
          'NAVIGATE_TAG_SPATIALLY',
          this.copyLinkPayload(request.tagId, facilityId),
        );
        if (!this.isCurrentCopyLinkRequest(request)) return;
        await this.applyCopyLinkOutcome(copyOutcome, request);
      } catch (err) {
        if (!this.isCurrentCopyLinkRequest(request)) return;
        debugError('Tag URL resolution failed', err);
        this.reportCopyLinkFailure();
      }
    },

    beginCopyLinkRequest() {
      const tagId = this.editingTag?.id;
      if (!tagId) return null;
      return { tagId, requestToken: ++this.copyLinkRequestToken };
    },

    isCurrentCopyLinkRequest({ tagId, requestToken }) {
      return requestToken === this.copyLinkRequestToken && tagId === this.editingTag?.id;
    },

    copyLinkPayload(tagId, selectedFacilityId = null) {
      return {
        tagId,
        intent: 'copy-link',
        selectedFacilityId,
        isolate: false,
        source: 'ui',
        assetId: this.$store.state.assetId,
      };
    },

    async applyCopyLinkOutcome(copyOutcome, request) {
      if (!this.isCurrentCopyLinkRequest(request)) return;
      if (this.isCopyLinkFailure(copyOutcome)) {
        this.reportCopyLinkFailure();
        return;
      }
      if (copyOutcome?.status === 'stale') return;
      if (copyOutcome?.status === 'choose-facility') {
        this.copyUrlAlternatives = copyOutcome.plan?.alternatives || [];
        this.showFacilityMenu = this.copyUrlAlternatives.length > 0;
        return;
      }
      if (copyOutcome?.status === 'resolved') {
        await this.copyResolvedPlan(copyOutcome.plan, request.tagId, request.requestToken);
      }
    },

    isCopyLinkFailure(copyOutcome) {
      return ['failed', 'unavailable'].includes(copyOutcome?.status);
    },

    reportCopyLinkFailure() {
      this.$dialog.notify.error('Unable to copy this tag link.', {
        position: 'top-right',
        timeout: 3000,
      });
    },

    tagUrlFromPlan(plan, tagId) {
      if (!plan?.facilityId || !tagId) return null;
      if (!['panel', 'viewer', 'navigate-facility'].includes(plan.kind)) return null;
      const action = plan.kind === 'panel'
        ? 'openPanel'
        : (plan.viewer === '360' ? 'zoom360' : 'isolate');
      return `${window.location.origin}/${plan.facilityId}/?tagId=${tagId}&${action}=true`;
    },

    async copyResolvedPlan(plan, tagId, requestToken) {
      const request = { tagId, requestToken };
      if (!this.isCurrentCopyLinkRequest(request)) return;
      const url = this.tagUrlFromPlan(plan, tagId);
      if (!url) {
        debugError('Cannot copy tag URL — resolved plan has no destination', plan);
        return;
      }
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        this.fallbackCopy(url);
      }
      if (!this.isCurrentCopyLinkRequest(request)) return;
      debugLog('Tag URL copied', { tagId, url });
      this.triggerCopyEffect();
      this.showFacilityMenu = false;
      this.copyUrlAlternatives = [];
    },

    facilityName(facilityId) {
      const facility = this.$store.getters.getFacilityDict?.[facilityId];
      return typeof facility === 'string' ? facility : (facility?.name || facilityId);
    },

    /**
     * Flip justCopied to true for 1.5s to drive the button's visual effect.
     * Cancels any pending revert-timer so rapid clicks keep the effect alive.
     */
    triggerCopyEffect() {
      this.justCopied = true;
      if (this.copyEffectTimer) clearTimeout(this.copyEffectTimer);
      this.copyEffectTimer = setTimeout(() => {
        this.justCopied = false;
        this.copyEffectTimer = null;
      }, 1500);
    },

    /**
     * Clipboard fallback for non-secure contexts (http://) or browsers
     * without navigator.clipboard. Uses the legacy execCommand('copy')
     * via a hidden textarea. Matches the pattern in ApiKeyCreatedDialog.vue.
     */
    fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    },
  },
};
</script>

<style lang="scss" scoped>
.content-panel {
  position: fixed;
  top: var(--ds-nav-top-bar-height);
  height: calc(100vh - var(--ds-nav-top-bar-height));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition:
    left var(--ds-transition-panel),
    width var(--ds-transition-panel);
  will-change: left, width;

  // Remove transition during resize for smoother experience
  &.is-resizing {
    transition: none;
  }

  // First panel has rounded left corners
  &.is-first {
    border-radius: 0;
  }

  // Last panel has right shadow
  &.is-last {
    box-shadow:
      4px 0 16px rgba(0, 0, 0, 0.2),
      1px 0 4px rgba(0, 0, 0, 0.1);
  }

  // Border between panels
  &:not(.is-first) {
    border-left: 1px solid var(--ds-border-subtle);
  }
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: var(--ds-space-xs);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

.loading-skeleton {
  padding: var(--ds-space-md);
}

// Copy Tag URL button styling - mirrors .pin-btn for visual consistency.
// On a successful copy, .copy-success is applied for ~1.5s, turning the
// icon green to confirm the action.
.copy-url-btn {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  min-height: 44px !important;
  border-radius: 4px !important;
  background: transparent !important;
  transition: all 0.15s ease !important;
  margin-right: 4px;

  .v-icon {
    color: var(--ds-text-subtle);
    transition: color 0.15s ease;
    font-size: 16px !important;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.06) !important;

    .v-icon {
      color: rgba(0, 0, 0, 0.7) !important;
    }
  }

  // Post-copy confirmation state
  &.copy-success {
    .v-icon {
      color: var(--v-secondary-base) !important;
    }

    &:hover .v-icon {
      color: var(--v-secondary-base) !important;
    }
  }
}

.copy-url-menu-item {
  min-height: 44px;
}

// Pin button styling - Simple rotation indicator
.pin-btn {
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  border-radius: 4px !important;
  background: transparent !important;
  transition: all 0.15s ease !important;
  margin-right: 4px;

  .v-icon {
    color: var(--ds-text-subtle);
    transition: transform 0.2s ease, color 0.15s ease;
    font-size: 16px !important;
    transform: rotate(0deg);
  }

  // Hover state
  &:hover {
    background: rgba(0, 0, 0, 0.06) !important;

    .v-icon {
      color: rgba(0, 0, 0, 0.7) !important;
    }
  }

  // Pinned state - rotated pin + green color
  &.pin-active {
    .v-icon {
      color: #03624c !important;
      transform: rotate(45deg);
    }

    &:hover .v-icon {
      color: #5C7A38 !important;
    }
  }
}

.panel-not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: var(--ds-space-lg);
  text-align: center;
}

// Mobile responsive
@media (max-width: 768px) {
  .content-panel {
    left: 0 !important;
    width: 100vw !important;
    height: 100vh;
    top: 0;
    border-radius: 0;
    z-index: 100 !important;
  }
}
</style>
